import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, AlertCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying');
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();

  const token = searchParams.get('token');

  useEffect(() => {
    if (token) {
      handleVerification();
    } else {
      setStatus('invalid');
    }
  }, [token]);

  const handleVerification = async () => {
    try {
      await axios.post(`${API}/auth/verify-email`, { token });
      setStatus('success');
      toast.success('Email verified successfully!');
    } catch (error) {
      console.error('Verification failed:', error);
      const message = error.response?.data?.detail || 'Verification failed';
      if (message.includes('expired')) {
        setStatus('expired');
      } else {
        setStatus('invalid');
      }
      toast.error(message);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/20 via-gray-950 to-gray-950 -z-10" />

      <Card className="max-w-md w-full bg-gray-900/80 border-white/10">
        <CardContent className="p-8 text-center">
          {status === 'verifying' && (
            <>
              <Loader2 className="w-16 h-16 text-blue-400 mx-auto mb-4 animate-spin" />
              <h2 className="text-2xl font-bold mb-2 font-['Outfit']">Verifying Email</h2>
              <p className="text-gray-400">Please wait...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold mb-2 font-['Outfit']">Email Verified!</h2>
              <p className="text-gray-400 mb-6">
                Your email has been successfully verified. You now have full access to Gibson Stratum.
              </p>
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                Go to Dashboard
              </Button>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
                <Mail className="w-10 h-10 text-yellow-400" />
              </div>
              <h2 className="text-2xl font-bold mb-2 font-['Outfit']">Link Expired</h2>
              <p className="text-gray-400 mb-6">
                This verification link has expired. Please request a new one from your dashboard.
              </p>
              <Link to="/login">
                <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  Sign In
                </Button>
              </Link>
            </>
          )}

          {status === 'invalid' && (
            <>
              <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-red-400" />
              </div>
              <h2 className="text-2xl font-bold mb-2 font-['Outfit']">Invalid Link</h2>
              <p className="text-gray-400 mb-6">
                This verification link is invalid or has already been used.
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full border-white/20">
                  Back to Login
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
